// AdminWidgetsSpec.sales-companies.js — TM/영업 회사별 위젯 entry (2026-06-08)
//
// 회사별 (PINE/DOT/LNJ): admin_tm_{cc} / admin_cancel_{cc} / admin_sales_{cc}
// 라벨 형식: "TM 오늘 콜 (PINE)" / "오늘 취소 (DOT)" / "오늘 카드 (LNJ)".

import {
  Phone, FileX, CreditCard, Award, CalendarCheck,
} from 'lucide-react';
import { WIDTHS, formatCompactKrw, truncateName } from './widgetFormat';
import React from 'react';

const fmt = (n) => Number(n || 0).toLocaleString();

// 오늘 미팅 list — L 사이즈 / 2열 grid (한 줄에 데이터 2개), 카테고리 분리 X.
function renderTodayBookings(bookings) {
  if (!Array.isArray(bookings) || bookings.length === 0) return null;
  return (
    <span className="grid grid-cols-2 gap-x-2 gap-y-0.5 w-full">
      {bookings.map((b, i) => {
        const c = truncateName(b.consultant || '?', 3);
        const m = truncateName(b.marketer || '?', 3);
        return (
          <span key={`bk-${i}`} className="flex items-baseline gap-0.5 text-[9px] text-[var(--text-muted)] min-w-0">
            <span className="truncate flex-shrink-0">{c}</span>
            <span className="flex-shrink-0">→</span>
            <span className="font-bold text-[var(--text-sub)] truncate flex-shrink-0">{m}</span>
            <span className="truncate flex-1">{b.region}</span>
            <span className="font-black text-sky-500 whitespace-nowrap flex-shrink-0">{b.count}건</span>
          </span>
        );
      })}
    </span>
  );
}

// 오늘 콜 (회사별) — 내일 방문 위젯과 동일 layout:
//   헤더 카운트 (콜수만) + 구분선 + 본문 list (한 줄에 2개 grid).
function renderTmCompany(d) {
  if (!d) return '-';
  const calls = Number(d?.calls || 0);
  return (
    <span className="w-full flex flex-col">
      {/* 헤더 카운트 — 내일 방문 패턴 (text-xl accent + "콜") */}
      <span className="flex items-baseline justify-end gap-1 mb-2">
        <span className="text-xl font-black tabular-nums leading-none text-sky-700 dark:text-sky-300">{fmt(calls)}</span>
        <span className="text-[10px] font-bold text-[var(--text-sub)] uppercase tracking-wider">콜</span>
      </span>
      {/* 구분선 */}
      <span className="block border-t border-sky-200 dark:border-sky-700 mx-1 my-1.5" />
      {/* 본문 list */}
      {renderTodayBookings(d.todayBookings)}
    </span>
  );
}

function renderCancelCompany(d) {
  if (!d) return '-';
  const items = Array.isArray(d.items) ? d.items : [];
  const count = Number(d?.count || 0);
  return (
    <span className="flex flex-col items-end gap-1">
      <span className="inline-flex items-baseline gap-1">
        <span className="text-3xl font-black tabular-nums leading-none text-rose-700 dark:text-rose-300">{fmt(count)}</span>
        <span className="text-[11px] font-semibold text-[var(--text-sub)]">건</span>
      </span>
      {items.length > 0 && (
        <span className="flex flex-wrap gap-x-1.5 gap-y-0.5 justify-end items-center mt-0.5">
          {items.slice(0, 3).map((it, i) => {
            const m = truncateName(it.marketer || '?', 3);
            const c = truncateName(it.consultant || '?', 3);
            const p = it.product || '';
            const t = Number(it.ticketCount || 0);
            const s = Number(it.serviceCount || 0);
            return (
              <span key={`cn-${i}`} className="text-[10px] text-[var(--text-muted)]">
                {m}/{c} {p} {t}장{s > 0 ? `·서${s}` : ''}
              </span>
            );
          })}
        </span>
      )}
    </span>
  );
}

function renderSalesCompany(d) {
  const count = Number(d?.count || 0);
  const total = Number(d?.total || 0);
  return (
    <span className="flex flex-col items-end gap-1">
      <span className="inline-flex items-baseline gap-1">
        <span className="text-3xl font-black tabular-nums leading-none text-emerald-700 dark:text-emerald-300">{fmt(count)}</span>
        <span className="text-[11px] font-semibold text-[var(--text-sub)]">건</span>
      </span>
      <span className="text-[11px] text-[var(--text-sub)] font-semibold tabular-nums">{formatCompactKrw(total)}</span>
    </span>
  );
}

const COMPANIES = ['PINE', 'DOT', 'LNJ'];

function tmEntry(cc) {
  return [`admin_tm_${cc.toLowerCase()}`, {
    Icon: Phone, label: `오늘 콜 (${cc})`, width: WIDTHS.WIDE, accent: 'sky', size: 'M',
    render: renderTmCompany,
    watermarkIcon: Phone,
  }];
}

function cancelEntry(cc) {
  return [`admin_cancel_${cc.toLowerCase()}`, {
    Icon: FileX, label: `이번주 취소 (${cc})`, width: WIDTHS.WIDE, accent: 'rose', size: 'S',
    render: renderCancelCompany,
    watermarkIcon: FileX,
  }];
}

function salesEntry(cc) {
  return [`admin_sales_${cc.toLowerCase()}`, {
    Icon: CreditCard, label: `오늘 매출 (${cc})`, width: WIDTHS.WIDE, accent: 'emerald', size: 'S',
    render: renderSalesCompany,
    watermarkIcon: CreditCard,
  }];
}

// 점수 (회사별) — 현재달 재직 직원 × 최근 6개월. 이번 달 열을 강조하고 그 달 점수 높은 순.
function renderTmScore(d) {
  if (!d || !Array.isArray(d.rows) || d.rows.length === 0) return '대상 없음';
  const months = d.months || [];
  const mLabel = (ym) => `${Number(String(ym).slice(5, 7))}월`;
  return (
    <span className="w-full flex flex-col gap-1">
      <span className="flex items-baseline justify-end gap-1">
        <span className="text-[9px] text-[var(--text-muted)]">{d.employeeCount}명 · 이번달 평균</span>
        <span className="text-lg font-black tabular-nums leading-none text-violet-600 dark:text-violet-300">
          {d.currentAverage === null || d.currentAverage === undefined ? '-' : d.currentAverage}
        </span>
      </span>
      <span className="grid gap-x-1 items-center"
        style={{ gridTemplateColumns: `minmax(38px,auto) repeat(${months.length}, minmax(0,1fr))` }}>
        <span className="text-[9px] text-[var(--text-muted)]" />
        {months.map((ym) => (
          <span key={`h-${ym}`}
            className={`text-[9px] text-right tabular-nums ${ym === d.currentMonth ? 'font-black text-violet-600 dark:text-violet-300' : 'text-[var(--text-muted)]'}`}>
            {mLabel(ym)}
          </span>
        ))}
        {d.rows.map((r) => (
          <React.Fragment key={`r-${r.userId}`}>
            <span className="text-[10px] font-bold text-[var(--text-sub)] truncate">{r.name}</span>
            {months.map((ym) => {
              const v = r.scores ? r.scores[ym] : null;
              const isCur = ym === d.currentMonth;
              return (
                <span key={`c-${r.userId}-${ym}`}
                  className={`text-[10px] text-right tabular-nums ${isCur ? 'font-black text-violet-600 dark:text-violet-300' : 'text-[var(--text-muted)]'}`}>
                  {v === null || v === undefined ? '-' : Math.round(v)}
                </span>
              );
            })}
          </React.Fragment>
        ))}
      </span>
    </span>
  );
}

function scoreEntry(cc) {
  return [`admin_tmscore_${cc.toLowerCase()}`, {
    Icon: Award, label: `점수 (${cc})`, width: WIDTHS.WIDE, accent: 'violet', size: 'L',
    render: renderTmScore,
    monthNavigate: true,
    watermarkIcon: Award,
  }];
}

// 잡은 미팅 3주 (회사별) — 직원 × 일별 21칸. 주 시작(월)에 구분선, 오늘 열 강조, 0은 흐리게.
function renderTmMeetWeeks(d) {
  if (!d || !Array.isArray(d.rows) || d.rows.length === 0) return '대상 없음';
  const days = d.days || [];
  return (
    <span className="w-full flex flex-col gap-1">
      <span className="flex items-baseline justify-end gap-1">
        <span className="text-[9px] text-[var(--text-muted)]">{d.employeeCount}명 · 3주 {fmt(d.total)}건 · 평균</span>
        <span className="text-lg font-black tabular-nums leading-none text-sky-700 dark:text-sky-300">
          {d.averageAll === null || d.averageAll === undefined ? '-' : d.averageAll.toFixed(1)}
        </span>
      </span>
      <span className="grid gap-x-px items-center"
        style={{ gridTemplateColumns: `minmax(34px,auto) repeat(${days.length}, minmax(0,1fr)) minmax(20px,auto)` }}>
        <span />
        {days.map((dy) => (
          <span key={`h-${dy.ymd}`}
            className="text-[8px] text-center tabular-nums leading-tight"
            style={{
              // 일요일 빨강 / 토요일 파랑 — 앱 달력(CalendarView)과 같은 색
              color: dy.dow === 0 ? '#ef4444' : dy.dow === 6 ? '#3b82f6' : 'var(--text-muted)',
              fontWeight: dy.ymd === d.today ? 900 : 400,
              ...(dy.isWeekStart ? { borderLeft: '1px solid var(--border)' } : {}),
            }}>
            {dy.day}
          </span>
        ))}
        <span className="text-[8px] text-right text-[var(--text-muted)]">평균</span>
        {d.rows.map((r) => (
          <React.Fragment key={`r-${r.userId}`}>
            <span className="text-[10px] font-bold text-[var(--text-sub)] truncate">{r.name}</span>
            {days.map((dy) => {
              const v = r.counts ? (r.counts[dy.ymd] ?? 0) : 0;
              const isToday = dy.ymd === d.today;
              return (
                <span key={`c-${r.userId}-${dy.ymd}`}
                  className={`text-[9px] text-center tabular-nums leading-tight ${
                    v === 0 ? 'text-[var(--text-muted)] opacity-30'
                      : isToday ? 'font-black text-sky-700 dark:text-sky-300'
                        : 'font-semibold text-[var(--text-sub)]'}`}
                  style={dy.isWeekStart ? { borderLeft: '1px solid var(--border)' } : undefined}>
                  {v === 0 ? '·' : v}
                </span>
              );
            })}
            <span className="text-[10px] text-right tabular-nums font-black text-sky-700 dark:text-sky-300">
              {r.average === null || r.average === undefined ? '-' : r.average.toFixed(1)}
            </span>
          </React.Fragment>
        ))}
      </span>
    </span>
  );
}

function meetWeeksEntry(cc) {
  return [`admin_tmmeet_${cc.toLowerCase()}`, {
    Icon: CalendarCheck, label: `잡은 미팅 3주 (${cc})`, width: WIDTHS.WIDE, accent: 'sky', size: 'L',
    render: renderTmMeetWeeks,
    watermarkIcon: CalendarCheck,
  }];
}

export const SALES_COMPANY_ENTRY_BUILDERS = {
  admin_tm: tmEntry,
  admin_cancel: cancelEntry,
  admin_sales: salesEntry,
  admin_tmscore: scoreEntry,
  admin_tmmeet: meetWeeksEntry,
};

export const SALES_COMPANY_SPECS = Object.fromEntries([
  ...COMPANIES.map(tmEntry),
  ...COMPANIES.map(cancelEntry),
  ...COMPANIES.map(salesEntry),
  ...COMPANIES.map(scoreEntry),
  ...COMPANIES.map(meetWeeksEntry),
]);
